import { ConfirmDialog } from "@/components/common/ConfirmDialog/ConfirmDialog"
import { EmptyState } from "@/components/common/EmptyState/EmptyState"
import { PermissionGate } from "@/components/common/PermissionGate"
import { toast } from "@/components/common/Toast"
import { Button } from "@/components/ui/button"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { PERMISSIONS } from "@/constants/permissions"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { AnalyticsEvent, trackEvent } from "@/services/analytics/amplitude"
import { IconAlertTriangle } from "@tabler/icons-react"
import { ArrowRight, Loader2, Radar, Settings } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import {
  apiErrorMessage,
  getApiErrorStatus,
  toastApiError,
} from "../../../api/apiError"
import {
  isRecipientOverlapError,
  isSupplierWarningError,
  recipientOverlaps,
  sendInquiryDirect,
  sendInquiryTest,
  signoffPlaceholderLines,
  supplierWarningNotes,
  useInquiryDraft,
  usePatchInquiry,
  type InquiryDraftPatch,
  type RecipientOverlap,
} from "../../../api/inquiryDraftApi"
import type { NoteOut } from "../../../api/notesApi"
import { useInquiryRegulationOptions } from "../../../api/supplierInquiriesApi"
import {
  DEFAULT_SCOPE_WARN_SUBJECTS,
  useTemplateSettings,
} from "../../../api/templateSettingsApi"
import { SupplierWarningDialog } from "../../shared/notes/SupplierWarningDialog"
import { StudioHeader } from "../../shared/StudioHeader"
import type { DraftArticle } from "../inquiry/draft/AddArticleDialog"
import { DraftComposerPanel } from "../inquiry/draft/DraftComposerPanel"
import { DraftPlaceholderNotice } from "../inquiry/draft/DraftPlaceholderNotice"
import { DraftPreviewPanel } from "../inquiry/draft/DraftPreviewPanel"
import {
  DraftSettingsSheet,
  type DraftSettingsTarget,
} from "../inquiry/draft/DraftSettingsSheet"
import {
  deriveInquiryDue,
  isoDateToUtcMidnight,
  swapInquiryArticleRows,
  type AttachmentFormat,
  type InquiryDraftItem,
  type Lang,
  type Stage,
} from "../inquiry/draft/inquiryDraftMock"
import type { RegulationEditor } from "../inquiry/draft/InquiryRegulationsNote"
import {
  SignoffPlaceholderDialog,
  signoffLinesToConfirm,
  type SignoffLines,
} from "../inquiry/draft/SignoffPlaceholderDialog"
import { RecipientOverlapDialog } from "../inquiry/RecipientOverlapDialog"
import { INQUIRY_BASE } from "../inquiry/routes"

const BTN_PRIMARY = "bg-yellow1 text-black hover:bg-yellow2"
const INQUIRY_LIST = INQUIRY_BASE

// Templates exist in EN/DE only: DE for a DE UI, else EN (incl. JA). Matches useCreateInquiries.
function inquiryLangFromLocale(currentLanguage: string): Lang {
  return currentLanguage === "DE" ? "de" : "en"
}

/** Everything the user confirmed for one draft so far; every retry carries all of it. */
interface SendAcks {
  noteIds?: string[]
  inquiryIds?: string[]
  signoff?: boolean
}

/** A draft whose send the server held back for the user's decision: its supplier's
 *  warning notes, other suppliers' inquiries to the same address, or a sign-off still
 *  holding placeholder text (when the composer's own check could not see it). */
interface HeldSend {
  inquiry: InquiryDraftItem
  notes: NoteOut[]
  overlaps: RecipientOverlap[]
  signoff: SignoffLines | null
  acks: SendAcks
}

/** An inquiry still in draft (a missing status reads as one). */
function isDraft(inquiry: InquiryDraftItem): boolean {
  return (inquiry.status ?? "draft") === "draft"
}

export function InquiryDraftPage() {
  const { translate, currentLanguage } = useTranslation()
  const localeLang = inquiryLangFromLocale(currentLanguage)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const ids = searchParams.getAll("ids")

  useEffect(() => {
    if (ids.length === 0) navigate(INQUIRY_LIST, { replace: true })
  }, [ids.length, navigate])

  const {
    data: draft,
    isLoading: draftLoading,
    error: draftError,
    refetch: refetchDraft,
  } = useInquiryDraft(ids)
  const patchInquiry = usePatchInquiry()
  // Bulk recipient edits round-trip through a PATCH + refetch before the new/removed
  // address shows up in `inquiries`, unlike single-supplier mode's instant local
  // state — "<inquiryId>:<field>" -> the address mid-PATCH, so a spinner can stand
  // in on that exact chip (removal) or the add control (add) until it settles.
  const [pendingRecipientAddresses, setPendingRecipientAddresses] = useState<
    Map<string, string>
  >(new Map())

  const inquiries = draft?.inquiries ?? []
  const bulk = inquiries.length > 1
  const primary = inquiries[0]
  // What a first-stage save (format, language, deadline, copy, regulations) goes to:
  // the batch's drafts, or the one inquiry if it is a draft. A sent one is frozen, and
  // the backend quietly ignores most of these fields on it, so writing them there made
  // the composer look as if the whole batch changed (TUR-1839).
  const draftTargets = (bulk ? inquiries : primary ? [primary] : []).filter(
    isDraft,
  )
  const frameworks = primary?.frameworks ?? []

  // Stage is fixed for the session and comes from the route (the inquiry detail
  // "Send reminder" action opens this page with ?stage=reminder). The user can't
  // switch it here — the composer shows a read-only badge instead.
  const stage: Stage =
    searchParams.get("stage") === "reminder" ? "reminder" : "first"
  const [to, setTo] = useState<string[]>([])
  const [cc, setCc] = useState<string[]>([])
  // Per-stage subject/body so each stage keeps its own copy. Switching stages just
  // swaps which slot is shown/edited and never overwrites the other stage's text —
  // so a first-request edit survives first → reminder → first.
  const [subjects, setSubjects] = useState<Record<Stage, string>>({
    first: "",
    reminder: "",
  })
  const [bodies, setBodies] = useState<Record<Stage, string>>({
    first: "",
    reminder: "",
  })
  const subject = subjects[stage]
  const body = bodies[stage]
  const setSubject = (v: string) =>
    setSubjects((prev) => ({ ...prev, [stage]: v }))
  const setBody = (v: string | ((prev: string) => string)) =>
    setBodies((prev) => ({
      ...prev,
      [stage]: typeof v === "function" ? v(prev[stage]) : v,
    }))
  const skipNextAutosaveRef = useRef(false)
  const [articles, setArticles] = useState<DraftArticle[]>([])
  // Blank until the inquiry loads, then seeded from its dueAt (or today + N days).
  const [due, setDue] = useState("")
  const [previewId, setPreviewId] = useState<string>("")
  // Questionnaire language drives which backend-rendered HTML the preview shows.
  // Defaults to DE; seeded from the persisted value per inquiry (below).
  const [language, setLanguage] = useState<Lang>(localeLang)
  // Attachment format (PDF vs Excel workbook). Defaults to pdf; seeded per inquiry below.
  const [format, setFormat] = useState<AttachmentFormat>("pdf")
  // Org inquiry settings may LOCK the attachment format (backend enforces it on
  // send/reminder/preview/download too): the composer's format select then shows the
  // enforced value read-only instead of offering a choice the send would ignore.
  const { data: templateSettings } = useTemplateSettings()
  const formatLocked = templateSettings?.attachmentFormatLocked ?? false
  const lockedFormat: AttachmentFormat =
    templateSettings?.defaultAttachmentFormat ?? "pdf"
  // What actually goes out — the org's enforced format when locked, else the user's
  // choice. Mirrors the backend's _effective_attachment_format: a "none" inquiry (a
  // declaration-review Rückfrage, or its reminder) stays "none" no matter what the
  // org locks, the same way the backend refuses to let an org-level lock override it.
  const effectiveFormat: AttachmentFormat =
    format === "none" ? "none" : formatLocked ? lockedFormat : format
  // The two-attachment package sends BOTH files, so the preview needs a way to look at
  // each. This tab is UI-ONLY: never persisted, never sent — the format is decided in
  // the composer. Any other format has exactly one file and shows no switcher.
  const showPreviewTabs = effectiveFormat === "pdf_and_excel"
  const [previewTab, setPreviewTab] = useState<"pdf" | "excel">("pdf")
  // The org setting open beside the draft (the signature from the header's gear, the
  // others from the composer's "Set in Settings" links): a sheet, not a navigation —
  // leaving the page would throw away the draft's unsaved subject/body edits.
  const [settingsSheet, setSettingsSheet] =
    useState<DraftSettingsTarget | null>(null)

  // Seed recipients + article list ONCE per inquiry id — not on every `primary`
  // reference change. react-query hands back a stable ref in prod, but guarding by
  // id makes this resilient: it won't re-run (and won't infinite-loop allocating a
  // fresh `?? []` array) if the source object identity ever changes between renders.
  const [seededDataId, setSeededDataId] = useState<string | null>(null)
  useEffect(() => {
    if (!primary || seededDataId === primary.id) return
    if (!bulk) {
      setTo(primary.contactEmails)
      // Seed Cc so a persisted Cc survives reload (otherwise the next autosave would
      // PATCH recipientsCc: [] back over the saved value).
      setCc(primary.contactEmailsCc ?? [])
    }
    setArticles(primary.articles)
    // Use the persisted deadline; fall back to today + the ORG'S window when none is
    // set (or the seeded one has lapsed) — the same window the backend seeded the draft
    // with, so the composer never shows a date the send would then move.
    setDue(deriveInquiryDue(primary.dueAt, templateSettings?.defaultDueDays))
    setSeededDataId(primary.id)
  }, [bulk, primary, seededDataId, templateSettings?.defaultDueDays])

  const [seededKey, setSeededKey] = useState<string | null>(null)
  useEffect(() => {
    if (!primary) return
    const key = `${primary.id}:${primary.attachmentFormat ?? ""}`
    if (seededKey === key) return
    if (seededKey !== null && seededKey.startsWith(`${primary.id}:`)) {
      skipNextAutosaveRef.current = true
    }
    const initialLanguage = primary.language ?? localeLang
    setSubjects({
      first: primary.emailCopy.first[initialLanguage].subject,
      reminder: primary.emailCopy.reminder[initialLanguage].subject,
    })
    setBodies({
      first: primary.emailCopy.first[initialLanguage].body,
      reminder: primary.emailCopy.reminder[initialLanguage].body,
    })
    setLanguage(initialLanguage)
    // "none" wins over the lock unconditionally: see effectiveFormat's own doc.
    setFormat(
      primary.attachmentFormat === "none"
        ? "none"
        : formatLocked
          ? lockedFormat
          : (primary.attachmentFormat ?? "pdf"),
    )
    setSeededKey(key)
  }, [primary, seededKey, localeLang, formatLocked, lockedFormat])

  // Format and language are batch-wide, so they persist to EVERY draft — unlike
  // recipients/copy/articles, which only apply to the editable primary. Never
  // writes "none": the ordinary composer has no way to choose it (the format
  // select is replaced with a read-only line while it's in effect, see
  // AttachmentLanguageSection), so a caller reaching this with it would be the
  // exact write the backend itself refuses.
  function persistComposerSetting(changes: InquiryDraftPatch) {
    if (stage === "reminder") return // frozen once sent
    const patch: InquiryDraftPatch =
      changes.attachmentFormat === "none"
        ? { ...changes, attachmentFormat: undefined }
        : changes
    if (draftTargets.length === 0) return
    // The questionnaire HTML is server-rendered per format; refetch once the batch
    // is saved, failed patches included.
    void inSaveOrder(() => patchEachDraft(draftTargets, patch)).then(() =>
      refetchDraft(),
    )
  }

  // Batch saves (format, language, regulations) run one batch after another, in the
  // order they were made: two quick format changes could otherwise interleave, the
  // older batch finishing last and leaving the drafts on different formats.
  // Idle, a batch starts at once; otherwise it waits for the one before it.
  const saveQueue = useRef<Promise<void>>(Promise.resolve())
  const savesInFlight = useRef(0)
  function inSaveOrder(task: () => Promise<void>): Promise<void> {
    const run =
      savesInFlight.current === 0 ? task() : saveQueue.current.then(task)
    savesInFlight.current += 1
    saveQueue.current = run
      .catch(() => undefined)
      .finally(() => {
        savesInFlight.current -= 1
      })
    return run
  }

  /**
   * Saves `changes` to each draft, one after another on the one guarded mutation, and
   * resolves when all are done (a failure is toasted once). Not `.mutate` per draft:
   * React Query runs a `mutate` call's own callbacks only for the LATEST call, so a
   * batch never saw its earlier drafts finish. Not concurrent `mutateAsync` either:
   * calls on one instance interfere (see the send fan-out below).
   */
  async function patchEachDraft(
    targets: readonly InquiryDraftItem[],
    changes: InquiryDraftPatch,
  ) {
    let firstError: unknown = null
    for (const inq of targets) {
      try {
        await patchInquiry.mutateAsync({ id: inq.id, changes })
      } catch (err) {
        firstError ??= err
      }
    }
    if (firstError) toastApiError(firstError, translate, "MC_API_ERROR_GENERIC")
  }

  // Settings load async: if the lock arrives after the draft seeded, the seeding
  // effect has already run — enforce the locked format whenever it is on so
  // autosave/send payloads never carry a stale choice. Never for "none": that
  // format is never a user/org choice to enforce, only a seeded server fact.
  useEffect(() => {
    if (formatLocked && format !== "none") setFormat(lockedFormat)
  }, [formatLocked, lockedFormat, format])

  function handleFormatChange(nextFormat: AttachmentFormat) {
    if (nextFormat === format) return
    skipNextAutosaveRef.current = true
    setFormat(nextFormat)
    persistComposerSetting({ attachmentFormat: nextFormat })
  }

  function handleLanguageChange(nextLanguage: Lang) {
    if (nextLanguage === language) return
    const nextCopy = primary?.emailCopy[stage][nextLanguage]
    skipNextAutosaveRef.current = true
    setLanguage(nextLanguage)
    if (nextCopy) {
      setSubject(nextCopy.subject)
      setBody(nextCopy.body)
    }
    persistComposerSetting({ language: nextLanguage })
  }

  function handleDueChange(nextDue: string) {
    if (nextDue === due) return
    // Derive before committing: a date we can't persist must not move local state either,
    // or the picker would drift from the saved deadline (and from the server-rendered
    // {{deadline}} in the preview).
    const dueAt = isoDateToUtcMidnight(nextDue)
    if (!dueAt) return
    setDue(nextDue)
    skipNextAutosaveRef.current = true
    persistComposerSetting({ dueAt })
  }

  useEffect(() => {
    if (primary && !previewId) setPreviewId(primary.id)
  }, [primary, previewId])

  // Read through a ref, never an effect dependency: a successful patch replaces the shared
  // draft-load cache with a freshly mapped array, so depending on this identity would
  // re-arm the autosave after every save and PATCH in a loop.
  const inquiriesRef = useRef(inquiries)
  inquiriesRef.current = inquiries

  // Debounced autosave (PATCH /{id}), waiting until the draft has been seeded so the
  // initial seeding does not itself trigger a save. Bulk saves the batch-wide copy to
  // EVERY draft; recipients and article scope are per-draft and skipped there.
  const seededId = primary ? primary.id : null
  const [seeded, setSeeded] = useState(false)
  useEffect(() => {
    setSeeded(false)
  }, [seededId])
  useEffect(() => {
    if (!seededId) return
    if (!seeded) {
      setSeeded(true)
      return
    }
    if (skipNextAutosaveRef.current) {
      skipNextAutosaveRef.current = false
      return
    }
    const handle = setTimeout(() => {
      // Copy persists only for the currently selected language; include language so
      // the backend knows which emailCopy cell this plain subject/body edit belongs to.
      const shared: InquiryDraftPatch = { language }
      if (stage === "reminder") {
        // The reminder subject is read-only (always "Re: <original>", forced server-side for
        // mail-client threading), so only the body is persisted — never the subject.
        shared.reminderEmailBody = bodies.reminder
      } else {
        shared.emailSubject = subjects.first
        shared.emailBody = bodies.first
        shared.dueAt = isoDateToUtcMidnight(due)
        // Format is frozen on send, so it's only editable (and persisted) pre-send.
        // "none" is never written back by the ordinary composer path: the backend
        // keeps it standing on its own and would refuse the write anyway.
        if (format !== "none") shared.attachmentFormat = format
      }
      if (bulk) {
        // Batch-wide fields only — recipients and article scope stay per-draft. The
        // first stage's copy and format go to the drafts only; the reminder body to
        // every inquiry of the batch.
        inquiriesRef.current
          .filter((inq) => stage === "reminder" || isDraft(inq))
          .forEach((inq) =>
            patchInquiry.mutate({ id: inq.id, changes: shared }),
          )
        return
      }
      // A single sent inquiry opened on the first stage: nothing of it is editable.
      if (stage !== "reminder" && primary && !isDraft(primary)) return
      const changes: InquiryDraftPatch = {
        ...shared,
        recipientsTo: to,
        recipientsCc: cc,
      }
      // Article scope is only editable on the first-request draft (the reminder
      // composer hides the editor). Never let a reminder autosave PATCH articleIds:
      // its `articles` state can be a stale cached copy (the load query is shared
      // across stages and served stale-then-fresh), so persisting it here would
      // silently reset the saved scope back to the pre-narrowing set.
      //
      // And only when the scope ACTUALLY changed. `articleIds` is the whole scope,
      // which on a large supplier is thousands of ids; sending it on the autosave
      // behind every keystroke meant a one-word subject edit shipped the entire list
      // and was rejected for being too long — on a draft the create path had seeded
      // with that many articles in the first place. Compared against the server's
      // copy rather than the `articlesChanged` below: that one also requires the
      // draft to be the one being PREVIEWED, and which tab is open must not decide
      // whether an edit is saved.
      if (stage !== "reminder") {
        const saved = primary?.articles ?? []
        const scopeChanged =
          articles.length !== saved.length ||
          articles.some((a, i) => a.id !== saved[i]?.id)
        if (scopeChanged) changes.articleIds = articles.map((a) => a.id)
      }
      patchInquiry.mutate({ id: seededId, changes })
    }, 800)
    return () => clearTimeout(handle)
    // `stage` is included so switching stage in place (same route, different
    // ?stage — React Router re-renders without remounting) cancels a pending
    // timer and reschedules under the current stage; otherwise a timer armed
    // just before the switch would fire with the old stage and defeat the
    // reminder articleIds guard above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    seededId,
    bulk,
    to,
    cc,
    subjects,
    bodies,
    articles,
    due,
    language,
    format,
    stage,
  ])

  const previewed = inquiries.find((i) => i.id === previewId) ?? primary

  // The backend returns the questionnaire with the table already filled. Only the
  // single-supplier FIRST-request draft is editable, so only there do we re-swap the
  // table rows (by id) when the list changes; otherwise we show the backend HTML
  // as-is. Reminders are excluded: their article scope can't change, and their
  // `articles` state may be a stale cached copy — swapping it in would overwrite the
  // correct backend-rendered reminder table with the wrong (pre-narrowing) rows.
  const editable =
    !bulk && !!primary && previewed?.id === primary.id && stage !== "reminder"
  const articlesChanged =
    editable &&
    (articles.length !== primary.articles.length ||
      articles.some((a, i) => a.id !== primary.articles[i]?.id))
  const previewHtml = useMemo(() => {
    if (!previewed) return undefined
    const base = previewed.questionnaire[stage][language]
    return articlesChanged
      ? swapInquiryArticleRows(base, articles, language)
      : base
  }, [previewed, stage, language, articlesChanged, articles])
  const previewLoading = draftLoading

  const [sendError, setSendError] = useState<string | null>(null)

  const [isSending, setIsSending] = useState(false)
  const [testSending, setTestSending] = useState(false)
  // Sends the server held back because the supplier has warning notes (409
  // SUPPLIER_WARNING, MC_NOTES) or another supplier's inquiry goes to the same
  // address (409 RECIPIENT_ALREADY_INQUIRED, TUR-1812): nothing went out for those,
  // so the user decides, warnings first. `acks` is everything confirmed for a draft
  // so far, carried by every retry. `sent` and `failed` count what went out, and
  // what failed, in the same run.
  const [pendingWarnings, setPendingWarnings] = useState<{
    entries: HeldSend[]
    sent: number
    failed: number
  } | null>(null)
  const warnedEntries =
    pendingWarnings?.entries.filter((entry) => entry.notes.length > 0) ?? []
  const overlapEntries =
    pendingWarnings?.entries.filter((entry) => entry.overlaps.length > 0) ?? []
  // The dialog on screen asks about these: warnings first, then overlaps, then
  // placeholder sign-offs.
  const askedEntries =
    warnedEntries.length > 0
      ? warnedEntries
      : overlapEntries.length > 0
        ? overlapEntries
        : (pendingWarnings?.entries ?? [])

  // Gmail-style soft warning: an empty subject/message doesn't block the send (the
  // supplier still gets the questionnaire PDF), it just asks for confirmation first.
  const [showEmptyConfirm, setShowEmptyConfirm] = useState(false)
  // The sign-off lines that still hold placeholder text, while the user decides
  // whether to send them as they are. `signoffConfirmed` rides on this click's sends.
  const [signoffLines, setSignoffLines] = useState<SignoffLines | null>(null)
  const signoffConfirmed = useRef(false)
  const [showLargeScopeConfirm, setShowLargeScopeConfirm] = useState(false)
  const [showRecipientConfirm, setShowRecipientConfirm] = useState(false)

  // True when the composer is missing copy we'd normally fill: the first request needs a
  // subject and a body; the reminder has no editable subject (it's always "Re: …"), only a
  // body. Bulk included — one editor covers the batch, so a cleared field blanks them all.
  function hasEmptyCopy(): boolean {
    if (!primary) return false
    return stage === "reminder"
      ? !bodies.reminder.trim()
      : !subjects.first.trim() || !bodies.first.trim()
  }

  // Settings > Regulations: a format covering none of the org's regulations is
  // refused on send (NO_INQUIRY_REGULATIONS). The composer says why
  // (InquiryRegulationsNote), so the buttons don't offer a send that can only fail.
  const regulationBlocked =
    stage !== "reminder" && inquiries.some((inq) => !!inq.regulationScopeError)

  // The drafts' regulations (TUR-1730): one picker for the batch, persisted to every
  // draft like the format; different regulations per supplier means separate
  // inquiries, so there is no per-supplier override. Only on the first
  // stage of a draft with documents, while regulation settings are on: once sent the
  // scope is frozen, and a document-less Rückfrage asks no regulation.
  const { data: regulationOptions } = useInquiryRegulationOptions()
  // Scope save batches still in flight: the send waits for them (the buttons stay off),
  // or a send could overtake the PATCH and freeze the previous regulations.
  const [scopeSaves, setScopeSaves] = useState(0)
  // Counts batches still saving (`patchEachDraft` awaits every draft of one).
  async function saveScope(
    targets: InquiryDraftItem[],
    changes: InquiryDraftPatch,
  ) {
    if (stage === "reminder" || targets.length === 0) return
    setScopeSaves((n) => n + 1)
    try {
      await inSaveOrder(() => patchEachDraft(targets, changes))
    } finally {
      setScopeSaves((n) => n - 1)
      void refetchDraft()
    }
  }
  // Drafts only: a sent inquiry's scope is frozen, and the backend ignores a change to
  // it, so a sent row of a partly sent batch (or a sent one opened here) is not offered.
  const scopeTargets = draftTargets
  // Only for who may save a draft (the route needs view only): without that a click
  // reaches the guarded PATCH and is refused, so the note stays read-only for them.
  const regulationEditor: RegulationEditor | undefined =
    stage !== "reminder" &&
    effectiveFormat !== "none" &&
    scopeTargets.length > 0 &&
    patchInquiry.canRun &&
    regulationOptions?.settingsOn
      ? {
          options: regulationOptions.regulations,
          onChange: (keys) =>
            void saveScope(scopeTargets, { regulationKeys: keys }),
          onFollowSettings: () =>
            void saveScope(scopeTargets, { followOrgRegulations: true }),
          // Any draft with its own pick offers the reset (it puts every draft back on
          // Settings): an assistant change to one draft must not be stuck there.
          explicit: scopeTargets.some((inq) => inq.scopeSource === "explicit"),
          // Sending turns the picker visibly off; a save in flight only holds clicks
          // (no "not allowed" flash on every tick), so none overtakes the save.
          disabled: isSending || testSending,
          busy: scopeSaves > 0,
          bulk,
          // A batch shares one pick, but a draft the assistant changed on its own can
          // still block the send: say so rather than leave Send off without a reason.
          blocked: scopeTargets.some((inq) => !!inq.regulationScopeError),
        }
      : undefined
  // A scope save still in flight blocks the send like an uncovered scope does.
  const sendBlocked = regulationBlocked || scopeSaves > 0

  function getSendableInquiries(): InquiryDraftItem[] {
    const isReminder = stage === "reminder"
    return isReminder
      ? inquiries
      : inquiries.filter((inq) => (inq.status ?? "draft") === "draft")
  }

  function getSendEmail(inq: InquiryDraftItem): string | undefined {
    if (!bulk && inq.id === primary?.id) return to[0]
    return inq.contactEmails[0]
  }

  // A draft above the org's scope warning size (Settings, default 500) is named before it
  // goes out: a supplier's whole linked list is rarely what one request is about.
  const scopeWarnAt =
    templateSettings?.scopeWarnSubjects ?? DEFAULT_SCOPE_WARN_SUBJECTS
  function largeScopeDrafts(): { name: string; count: number }[] {
    if (stage === "reminder") return []
    return getSendableInquiries()
      .map((inq) => ({
        name: inq.supplierName,
        count:
          !bulk && inq.id === primary?.id
            ? articles.length
            : inq.articles.length,
      }))
      .filter((draft) => draft.count > scopeWarnAt)
  }

  // The confirms chain, each one its own question: blank copy, then size, then recipients.
  function continueAfterCopyCheck() {
    if (largeScopeDrafts().length > 0) {
      setShowLargeScopeConfirm(true)
      return
    }
    continueAfterScopeCheck()
  }

  function continueAfterScopeCheck() {
    // A bulk send mails several real suppliers at once from one click. Name them
    // (and how many) before anything leaves — the same reason the declaration send
    // now confirms its recipients (F23). A single draft shows its recipient chips
    // on this page already, so it does not need the extra step.
    if (bulk) {
      setShowRecipientConfirm(true)
      return
    }
    void performSend()
  }

  async function handleSend() {
    if (isSending || sendBlocked) return
    const sendable = getSendableInquiries()
    if (sendable.some((inq) => !getSendEmail(inq))) {
      toast.error(translate("MC_INQ_DRAFT_EMAIL_MISSING"))
      return
    }
    signoffConfirmed.current = false
    const lines =
      stage === "reminder" || !templateSettings
        ? null
        : signoffLinesToConfirm(templateSettings, language, effectiveFormat)
    if (lines) {
      setSignoffLines(lines)
      return
    }
    continueAfterSignoffCheck()
  }

  function continueAfterSignoffCheck() {
    // Warn before sending blank copy, but let the user proceed (we don't refill from
    // the template behind their back, and the backend respects what's sent).
    if (hasEmptyCopy()) {
      setShowEmptyConfirm(true)
      return
    }
    continueAfterCopyCheck()
  }

  // One draft's send. `acks` is set only on a retry after the user confirmed the
  // supplier's warning notes or another inquiry to the same address.
  async function sendOne(
    inq: InquiryDraftItem,
    isReminder: boolean,
    dueAtIso: string | undefined,
    acks: SendAcks = {},
  ) {
    // Recipients stay per-draft: bulk lists each supplier's own addresses.
    const isPrimaryEditable = !bulk && inq.id === primary?.id
    // The send reads the saved scope, and the autosave lags 800 ms behind an edit:
    // save the articles on screen first, so the supplier gets exactly that list.
    if (isPrimaryEditable && !isReminder) {
      const saved = inq.articles
      if (
        articles.length !== saved.length ||
        articles.some((a, i) => a.id !== saved[i]?.id)
      ) {
        await patchInquiry.mutateAsync({
          id: inq.id,
          changes: { articleIds: articles.map((a) => a.id) },
        })
      }
    }
    // Send the full To/Cc (not just the first) so a send beating the autosave keeps them.
    const recipientsTo = isPrimaryEditable ? to : inq.contactEmails
    const recipientsCc = isPrimaryEditable ? cc : inq.contactEmailsCc
    const emailTo = recipientsTo[0] ?? undefined
    // One subject/body editor covers the whole batch, so its text goes to every
    // draft; bulk edits used to be dropped for each draft's stored copy. Supplier
    // tokens still resolve per inquiry server-side.
    const currentStageCopy = isReminder
      ? {
          // No reminderSubject: the outbound subject is always "Re: <original>",
          // forced server-side for threading, so the composer never sends one.
          reminderBody: bodies.reminder,
        }
      : { emailSubject: subjects.first, emailBody: bodies.first }
    const sent = await sendInquiryDirect(inq.id, {
      isReminder,
      dueAt: dueAtIso,
      emailTo,
      recipientsTo,
      recipientsCc,
      language,
      // "none" is never sent as a value: the backend keeps it standing on its
      // own (create_declaration_inquiry set it; reminders inherit it), and an
      // explicit "none" here would be the ordinary composer's send path
      // attempting the same write the backend already refuses on PATCH.
      ...(format !== "none" ? { attachmentFormat: format } : {}),
      ...currentStageCopy,
      ...(acks.noteIds ? { acknowledgedNoteIds: acks.noteIds } : {}),
      ...(acks.inquiryIds ? { acknowledgedInquiryIds: acks.inquiryIds } : {}),
      ...((signoffConfirmed.current || acks.signoff) && !isReminder
        ? { acknowledgePlaceholderSignature: true }
        : {}),
    })
    trackEvent(AnalyticsEvent.SUPPLIER_INQUIRY_SENT, {
      stage: isReminder ? "reminder" : "first",
      format,
      language,
      bulk,
    })
    return sent
  }

  // What to do once a round of sends has settled: sort the refusals into the ones
  // that wait for the user's decision (a supplier warning or a recipient overlap)
  // and the real failures, then either report the failure or leave the page.
  // `acks` is what each attempted draft carried; `waiting` are held drafts this round
  // did not retry (the overlaps behind a warning dialog).
  async function settleSend(
    attempted: InquiryDraftItem[],
    results: PromiseSettledResult<unknown>[],
    alreadySent: number,
    alreadyFailed: number,
    acks: SendAcks[] = [],
    waiting: HeldSend[] = [],
  ) {
    const isReminder = stage === "reminder"
    const warned: HeldSend[] = []
    const failures: PromiseRejectedResult[] = []
    results.forEach((result, i) => {
      if (result.status !== "rejected") return
      const held = { inquiry: attempted[i], acks: acks[i] ?? {} }
      if (isSupplierWarningError(result.reason)) {
        warned.push({
          ...held,
          notes: supplierWarningNotes(result.reason),
          overlaps: [],
          signoff: null,
        })
      } else if (isRecipientOverlapError(result.reason)) {
        warned.push({
          ...held,
          notes: [],
          overlaps: recipientOverlaps(result.reason),
          signoff: null,
        })
      } else if (signoffPlaceholderLines(result.reason)) {
        warned.push({
          ...held,
          notes: [],
          overlaps: [],
          signoff: signoffPlaceholderLines(result.reason),
        })
      } else {
        failures.push(result)
      }
    })
    warned.push(...waiting)

    const failedTotal = alreadyFailed + failures.length
    if (failedTotal > 0 || warned.length > 0) await refetchDraft()
    if (warned.length > 0) {
      setPendingWarnings({
        entries: warned,
        sent:
          alreadySent +
          attempted.length -
          (warned.length - waiting.length) -
          failures.length,
        failed: failedTotal,
      })
    }
    if (failures.length > 0) {
      // The banner outlives the toast, so it carries the same specific reason
      // (a 422 names the field that has to change) rather than a bare failure.
      const reason = apiErrorMessage(
        failures[0].reason,
        translate,
        "MC_INQ_DRAFT_SEND_FAILED",
      )
      toastApiError(failures[0].reason, translate, "MC_INQ_DRAFT_SEND_FAILED")
      setSendError(reason)
      return
    }
    // Waiting on the warning dialog: the page stays until the user has chosen.
    if (warned.length > 0) return
    // A failure earlier in the run (its banner is still up) keeps the user here too,
    // where it can be fixed: leaving would bury it.
    if (failedTotal > 0) return

    // Straight back to the inquiry list with a success toast, no extra success page.
    toast.success(
      translate(
        isReminder ? "MC_INQ_REMINDER_SENT" : "MC_INQ_DRAFT_SUCCESS_TITLE",
      ),
    )
    navigate(INQUIRY_LIST, { replace: true })
  }

  async function performSend() {
    if (isSending) return
    setIsSending(true)
    setSendError(null)
    try {
      const isReminder = stage === "reminder"
      const sendable = getSendableInquiries()
      if (sendable.length === 0) {
        // Nothing left to send (already sent) — just return to the list quietly.
        navigate(INQUIRY_LIST, { replace: true })
        return
      }

      const dueAtIso = !isReminder ? isoDateToUtcMidnight(due) : undefined

      // Fan out: one independent fetch per inquiry — avoids concurrent mutateAsync
      // on the same mutation instance which serialises/interferes in React Query.
      const results = await Promise.allSettled(
        sendable.map((inq) => sendOne(inq, isReminder, dueAtIso)),
      )
      await settleSend(sendable, results, 0, 0)
    } finally {
      setIsSending(false)
    }
  }

  // "Send anyway" for the dialog on screen: the warning dialog retries the drafts
  // held for a warning, the overlap dialog the rest. Each retry carries everything
  // confirmed for that draft so far. A note or overlap that appeared since, or an
  // overlap behind a confirmed warning, holds the retry again and the dialog comes back.
  async function sendDespiteWarnings() {
    if (!pendingWarnings || isSending) return
    const { entries, sent, failed } = pendingWarnings
    const asked = askedEntries
    const waiting = entries.filter((entry) => !asked.includes(entry))
    const acks = asked.map(({ notes, overlaps, signoff, acks }) => ({
      ...acks,
      ...(notes.length > 0 ? { noteIds: notes.map((note) => note.id) } : {}),
      ...(overlaps.length > 0
        ? { inquiryIds: [...new Set(overlaps.map((other) => other.inquiryId))] }
        : {}),
      ...(signoff ? { signoff: true } : {}),
    }))
    setIsSending(true)
    try {
      const isReminder = stage === "reminder"
      const dueAtIso = !isReminder ? isoDateToUtcMidnight(due) : undefined
      const results = await Promise.allSettled(
        asked.map(({ inquiry }, i) =>
          sendOne(inquiry, isReminder, dueAtIso, acks[i]),
        ),
      )
      setPendingWarnings(null)
      await settleSend(
        asked.map((entry) => entry.inquiry),
        results,
        sent,
        failed,
        acks,
        waiting,
      )
    } finally {
      setIsSending(false)
    }
  }

  // Cancel and skip both leave the refused drafts as they are, and both say so: a
  // plain "sent" toast would be wrong while a draft was held back, also when the
  // rest of the run went out. When other drafts already went out in this run and
  // nothing failed, it is over, so the page hands back to the list (a reminder run
  // would otherwise resend to everyone on the next click). A failure keeps the user
  // here with its banner, and with nothing sent there is nothing to hand back.
  // `stay` is for an edit: the user is fixing what held the drafts, so the page stays
  // even when the rest of the run went out.
  function leaveWarnings({ stay = false }: { stay?: boolean } = {}) {
    if (!pendingWarnings) return
    const { entries, sent, failed } = pendingWarnings
    // Leaving the warning dialog does not touch the drafts held for an overlap:
    // their dialog comes next, they were never shown.
    const left = askedEntries
    const waiting = entries.filter((entry) => !left.includes(entry))
    toast.warning(
      translate(
        warnedEntries.length > 0
          ? "MC_NOTE_SEND_SKIPPED"
          : "MC_INQ_OVERLAP_SKIPPED",
        { count: left.length },
      ),
    )
    if (waiting.length > 0) {
      setPendingWarnings({ entries: waiting, sent, failed })
      return
    }
    setPendingWarnings(null)
    if (!stay && sent > 0 && failed === 0)
      navigate(INQUIRY_LIST, { replace: true })
  }

  function handleBulkSupplierEmailAdded(inquiryId: string, email: string) {
    // `mutateAsync`, not `mutate` callbacks: React Query runs those only for the latest
    // call, so an autosave or a regulation save right after would drop the refetch.
    void patchInquiry
      .mutateAsync({ id: inquiryId, changes: { recipientsTo: [email] } })
      .then(
        () => void refetchDraft(),
        () => undefined, // as before: the API client toasts a 5xx; a 4xx stays quiet here
      )
  }

  function handleBulkRecipientsChanged(
    inquiryId: string,
    field: "recipientsTo" | "recipientsCc",
    next: string[],
    address: string,
  ) {
    const key = `${inquiryId}:${field}`
    setPendingRecipientAddresses((prev) => new Map(prev).set(key, address))
    const clearPending = () =>
      setPendingRecipientAddresses((prev) => {
        // Evict only THIS call's own entry — the row is locked while any address is
        // pending (see Row's rowLocked), so this shouldn't fire while a newer call
        // already overwrote the key, but a stale request settling after a retry or
        // some other edge case must not clear a newer pending entry it doesn't own.
        if (prev.get(key) !== address) return prev
        const rest = new Map(prev)
        rest.delete(key)
        return rest
      })
    // `mutateAsync`, not `mutate` callbacks: React Query runs those only for the latest
    // call, so a later save on the same mutation left the spinner and row lock stuck.
    // The spinner only covers the PATCH itself; the trailing refetch (which is what
    // actually brings the new/removed address into `inquiries`) runs quietly.
    void patchInquiry
      .mutateAsync({ id: inquiryId, changes: { [field]: next } })
      .then(
        () => void refetchDraft(),
        () => undefined, // as before: the API client toasts a 5xx; a 4xx stays quiet here
      )
      .finally(clearPending)
  }

  if (!primary) {
    // A draft that is GONE, not one still arriving. The load retries 404s five
    // times to ride out replica lag on a just-created row, which means a deleted
    // id produces a burst of 404s and then this screen — and this screen used to
    // be a blank card with no message, so the user sat on an editor that would
    // never fill (A5, three GETs and a DELETE on a dead id, 2026-09-22).
    if (getApiErrorStatus(draftError) === 404) {
      return (
        <div className="flex h-full items-center justify-center bg-card">
          <EmptyState
            title={translate("MC_INQ_DRAFT_GONE_TITLE")}
            description={translate("MC_INQ_DRAFT_GONE_DESC")}
            primaryAction={{
              label: translate("MC_INQ_DRAFT_GONE_BACK"),
              onClick: () => navigate(INQUIRY_LIST, { replace: true }),
            }}
          />
        </div>
      )
    }
    return <div className="flex h-full items-center justify-center bg-card" />
  }

  const supplierCount = inquiries.length
  // What the documents actually ask once Settings > Regulations narrowed them (the
  // composer lists the same set); the stored framework labels otherwise.
  const fwCount = primary?.askedRegulations
    ? primary.askedRegulations.length
    : frameworks.length

  return (
    <div
      data-agent-target="inquiry.draft"
      className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-card"
    >
      <StudioHeader
        onBack={() => navigate(INQUIRY_LIST)}
        backLabel={translate("MC_INQ_DRAFT_BTN_CANCEL")}
        title={
          bulk
            ? translate("MC_INQ_DRAFT_SUPPLIERS_COUNT", { n: supplierCount })
            : primary.supplierName
        }
        context={
          <>
            <Radar className="h-3.5 w-3.5 text-yellow1" />
            {bulk
              ? translate("MC_INQ_DRAFT_BULK_CTX")
              : // Framework list hidden for the moment — was MC_INQ_DRAFT_SINGLE_CTX
                // interpolated with frameworks.join(" · "); see git history to restore.
                translate("MC_INQ_NEW_TITLE")}
          </>
        }
      >
        <div className="flex items-center gap-2">
          {stage !== "reminder" && (
            <>
              {/* Preview-only: which of the package's two attachments is on screen.
                  The format itself is chosen in the composer
                  (AttachmentLanguageSection). */}
              {showPreviewTabs && (
                <Tabs
                  value={previewTab}
                  onValueChange={(v) => setPreviewTab(v as "pdf" | "excel")}
                >
                  <TabsList className="h-7 p-0.5">
                    <TabsTrigger
                      value="pdf"
                      className="px-2.5 py-0.5 text-[11px]"
                    >
                      {translate("MC_INQ_DRAFT_FORMAT_PDF")}
                    </TabsTrigger>
                    <TabsTrigger
                      value="excel"
                      className="px-2.5 py-0.5 text-[11px]"
                    >
                      {translate("MC_INQ_DRAFT_FORMAT_EXCEL")}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              )}
              {/* Same `language` state the composer's select writes — the two stay
                  in sync because there is only one source of truth. */}
              <Tabs
                value={language}
                onValueChange={(v) => handleLanguageChange(v as Lang)}
              >
                <TabsList className="h-7 p-0.5">
                  <TabsTrigger value="de" className="px-2.5 py-0.5 text-[11px]">
                    {translate("MC_INQ_DRAFT_LANG_DE")}
                  </TabsTrigger>
                  <TabsTrigger value="en" className="px-2.5 py-0.5 text-[11px]">
                    {translate("MC_INQ_DRAFT_LANG_EN")}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </>
          )}
          {/* Opens the org's signature settings — shown regardless of stage since a
              reminder's body gets the same backend-appended signature as a first
              request. Gated on settings having LOADED, not on the current language
              already having a signature — an empty one is exactly the case where the
              user needs this button, to add one, without navigating away and losing
              unsaved draft edits. */}
          {templateSettings && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={translate("MC_INQ_DRAFT_EDIT_SIGNATURE")}
              onClick={() => setSettingsSheet("email.signature")}
              className="h-7 w-7 text-grey5 hover:bg-transparent hover:text-grey6"
            >
              <Settings className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </StudioHeader>

      <div className="min-h-0 flex-1">
        <ResizablePanelGroup
          orientation="horizontal"
          autoSaveId="inquiry-draft-panels"
        >
          <ResizablePanel id="composer" defaultSize={48} minSize={32}>
            <DraftComposerPanel
              bulk={bulk}
              inquiries={inquiries}
              primary={primary}
              frameworks={frameworks}
              stage={stage}
              language={language}
              to={to}
              cc={cc}
              setTo={setTo}
              setCc={setCc}
              subject={subject}
              setSubject={setSubject}
              body={body}
              setBody={setBody}
              articles={articles}
              setArticles={setArticles}
              due={due}
              setDue={handleDueChange}
              format={effectiveFormat}
              setFormat={handleFormatChange}
              setLanguage={handleLanguageChange}
              onBulkSupplierEmailAdded={handleBulkSupplierEmailAdded}
              onBulkRecipientsChanged={handleBulkRecipientsChanged}
              pendingBulkRecipientAddresses={pendingRecipientAddresses}
              onEditSignature={() => setSettingsSheet("email.signature")}
              onOpenSettings={setSettingsSheet}
              regulationEditor={regulationEditor}
              regulationDraft={regulationEditor ? scopeTargets[0] : undefined}
            />
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel id="preview" defaultSize={52} minSize={30}>
            <DraftPreviewPanel
              bulk={bulk}
              html={previewHtml}
              isLoading={previewLoading}
              inquiries={inquiries}
              previewId={previewId}
              setPreviewId={setPreviewId}
              language={language}
              // The package renders one file at a time: "excel" shows/downloads the
              // workbook, "pdf_and_excel" shows/downloads only the short
              // Declaration-of-Conformity PDF side — the Download button here never
              // fetches both files at once. Every other format previews itself.
              format={
                showPreviewTabs
                  ? previewTab === "excel"
                    ? "excel"
                    : "pdf_and_excel"
                  : effectiveFormat
              }
              stage={stage}
              settingsStatus={primary.settingsStatus}
              onRetrySettings={() => void refetchDraft()}
              notice={
                <DraftPlaceholderNotice
                  language={language}
                  format={effectiveFormat}
                  onFix={setSettingsSheet}
                />
              }
            />
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      <div className="flex items-center gap-3 border-t border-grey4 px-6 py-3">
        {/* The autosave's state, so a user need not reopen the draft to trust it. */}
        <span
          role="status"
          className={`text-[11.5px] ${patchInquiry.isError ? "text-red1" : "text-grey5"}`}
        >
          {patchInquiry.isPending
            ? translate("MC_INQ_DRAFT_SAVING")
            : patchInquiry.isError
              ? translate("MC_INQ_DRAFT_SAVE_FAILED")
              : patchInquiry.isSuccess
                ? translate("MC_INQ_DRAFT_ALL_SAVED")
                : null}
        </span>
        <span className="flex-1" />
        {/* Summary hidden in bulk view — a per-draft count is confusing across many. */}
        {!bulk && (
          <span className="text-[11.5px] text-grey5">
            {translate(
              supplierCount === 1
                ? "MC_INQ_DRAFT_SUMMARY_SUPPLIER"
                : "MC_INQ_DRAFT_SUMMARY_SUPPLIERS",
              { n: supplierCount },
            )}{" "}
            ·{" "}
            {translate(
              fwCount === 1
                ? "MC_INQ_DRAFT_SUMMARY_FRAMEWORK"
                : "MC_INQ_DRAFT_SUMMARY_FRAMEWORKS",
              { n: fwCount },
            )}
          </span>
        )}
        {sendError && (
          <span className="text-danger text-[11.5px]">{sendError}</span>
        )}
        <Button variant="outline" onClick={() => navigate(INQUIRY_LIST)}>
          {translate("MC_INQ_DRAFT_BTN_CANCEL")}
        </Button>
        {/* Single-draft only: a bulk test would mail one copy per supplier draft
            to the same person. Passes no recipients — the server addresses the
            caller from the token, and the draft keeps its reference-less state so
            a reply cannot thread back (F15/F23). */}
        {/* Same gate as the real send: `sendInquiryTest` is a direct request with no
            mutation guard, so a view-only user must not see this at all. */}
        {!bulk && stage !== "reminder" && primary?.id && (
          <PermissionGate permission={PERMISSIONS.MC_INQUIRIES_SEND}>
            <Button
              variant="outline"
              disabled={isSending || testSending || sendBlocked}
              onClick={async () => {
                setTestSending(true)
                try {
                  const { sentTo } = await sendInquiryTest(primary.id)
                  toast.success(
                    translate("MC_INQ_DRAFT_SEND_TEST_OK", { email: sentTo }),
                  )
                } catch {
                  toast.error(translate("MC_INQ_DRAFT_SEND_TEST_ERR"))
                } finally {
                  setTestSending(false)
                }
              }}
            >
              {testSending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {translate("MC_INQ_DRAFT_SEND_TEST")}
            </Button>
          </PermissionGate>
        )}
        <PermissionGate
          permission={
            stage === "reminder"
              ? PERMISSIONS.MC_INQUIRIES_REMIND
              : PERMISSIONS.MC_INQUIRIES_SEND
          }
        >
          <Button
            data-agent-target="inquiry.send-button"
            className={BTN_PRIMARY}
            onClick={handleSend}
            disabled={isSending || sendBlocked}
          >
            {stage === "reminder"
              ? translate("MC_INQ_SEND_REMINDER")
              : bulk
                ? translate("MC_INQ_DRAFT_SEND_MANY", { n: supplierCount })
                : translate("MC_INQ_DRAFT_SEND_ONE")}
            {isSending ? (
              <Loader2 className="ml-2 h-4 w-4 animate-spin" />
            ) : (
              <ArrowRight className="ml-2 h-4 w-4" />
            )}
          </Button>
        </PermissionGate>
      </div>

      {pendingWarnings && warnedEntries.length > 0 && (
        <SupplierWarningDialog
          // The run's shape, not the count: a batch where only one supplier is
          // warned still offers to skip it and carry on.
          mode={bulk ? "bulk" : "single"}
          warnings={warnedEntries.map(({ inquiry, notes }) => ({
            inquiryId: inquiry.id,
            supplierName: inquiry.supplierName,
            notes,
          }))}
          onConfirm={() => void sendDespiteWarnings()}
          onSkip={() => leaveWarnings()}
          onCancel={() => leaveWarnings()}
          isLoading={isSending}
        />
      )}
      {pendingWarnings &&
        warnedEntries.length === 0 &&
        overlapEntries.length === 0 && (
          <SignoffPlaceholderDialog
            lines={{
              signature: [
                ...new Set(
                  askedEntries.flatMap((e) => e.signoff?.signature ?? []),
                ),
              ],
              footer: [
                ...new Set(
                  askedEntries.flatMap((e) => e.signoff?.footer ?? []),
                ),
              ],
            }}
            onSendAnyway={() => void sendDespiteWarnings()}
            onEdit={(target) => {
              leaveWarnings({ stay: true })
              setSettingsSheet(target)
            }}
            onCancel={() => leaveWarnings()}
            isLoading={isSending}
          />
        )}
      {pendingWarnings &&
        warnedEntries.length === 0 &&
        overlapEntries.length > 0 && (
          <RecipientOverlapDialog
            mode={bulk ? "bulk" : "single"}
            entries={overlapEntries.map(({ inquiry, overlaps }) => ({
              inquiryId: inquiry.id,
              supplierName: inquiry.supplierName,
              overlaps,
            }))}
            onConfirm={() => void sendDespiteWarnings()}
            onSkip={() => leaveWarnings()}
            onCancel={() => leaveWarnings()}
            isLoading={isSending}
          />
        )}

      {showRecipientConfirm && (
        <ConfirmDialog
          variant="warning"
          icon={IconAlertTriangle}
          title={translate("MC_INQ_DRAFT_CONFIRM_TITLE", {
            n: getSendableInquiries().length,
          })}
          details={
            <ul className="flex flex-col gap-2">
              {getSendableInquiries().map((inq) => (
                <li
                  key={inq.id}
                  className="rounded-md border border-grey4 bg-grey3 px-3 py-2"
                >
                  <div className="text-[13px] font-medium text-white">
                    {inq.supplierName}
                  </div>
                  <div className="text-[12px] break-all text-grey5">
                    {getSendEmail(inq) ?? "—"}
                  </div>
                </li>
              ))}
            </ul>
          }
          confirmLabel={translate("MC_INQ_DRAFT_CONFIRM_SEND", {
            n: getSendableInquiries().length,
          })}
          cancelLabel={translate("MC_INQ_DRAFT_BTN_CANCEL")}
          onConfirm={() => {
            setShowRecipientConfirm(false)
            void performSend()
          }}
          onCancel={() => setShowRecipientConfirm(false)}
        />
      )}

      {signoffLines && (
        <SignoffPlaceholderDialog
          lines={signoffLines}
          onSendAnyway={() => {
            setSignoffLines(null)
            signoffConfirmed.current = true
            continueAfterSignoffCheck()
          }}
          onEdit={(target) => {
            setSignoffLines(null)
            setSettingsSheet(target)
          }}
          onCancel={() => setSignoffLines(null)}
        />
      )}

      {showEmptyConfirm && (
        <ConfirmDialog
          variant="warning"
          icon={IconAlertTriangle}
          title={translate("MC_INQ_DRAFT_EMPTY_TITLE")}
          message={
            bulk
              ? translate("MC_INQ_DRAFT_EMPTY_COPY_BULK", {
                  n: getSendableInquiries().length,
                })
              : translate("MC_INQ_DRAFT_EMPTY_COPY")
          }
          confirmLabel={translate("MC_INQ_DRAFT_EMPTY_SEND")}
          cancelLabel={translate("MC_INQ_DRAFT_BTN_CANCEL")}
          onConfirm={() => {
            setShowEmptyConfirm(false)
            // Chain, don't skip: agreeing to blank copy is not agreeing to the
            // article count or the recipient list.
            continueAfterCopyCheck()
          }}
          onCancel={() => setShowEmptyConfirm(false)}
        />
      )}

      {showLargeScopeConfirm && (
        <ConfirmDialog
          variant="warning"
          icon={IconAlertTriangle}
          title={translate("MC_INQ_DRAFT_LARGE_SCOPE_TITLE")}
          message={translate("MC_INQ_DRAFT_LARGE_SCOPE_COPY", {
            drafts: largeScopeDrafts()
              .map((draft) => `${draft.name} (${draft.count})`)
              .join(", "),
            limit: scopeWarnAt,
          })}
          confirmLabel={translate("MC_INQ_DRAFT_LARGE_SCOPE_SEND")}
          cancelLabel={translate("MC_INQ_DRAFT_BTN_CANCEL")}
          onConfirm={() => {
            setShowLargeScopeConfirm(false)
            continueAfterScopeCheck()
          }}
          onCancel={() => setShowLargeScopeConfirm(false)}
        />
      )}

      <DraftSettingsSheet
        target={settingsSheet}
        onClose={() => setSettingsSheet(null)}
      />
    </div>
  )
}
